import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { ScreenHeader } from './poyais-ui/shell';
import { Button } from './poyais-ui/primitives';
import { TrelloBridgeModal, TrelloBoardPickerModal, useTrelloStatus, useTrelloOAuthResult, connectTrello } from './TrelloConnect';
import TrelloBoardDetail from './TrelloBoardDetail';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Görevler': 'Tasks',
  'Pazarlama görevlerini planla, organize et ve takip et.': 'Plan, organize, and track your marketing tasks.',
  'Yeni Görev': 'New Task',
  'Görevlerde ara…': 'Search tasks…',
  'Atanan': 'Assignee',
  'Durum': 'Status',
  'Öncelik': 'Priority',
  'Pano': 'Board',
  'Trello Panoları': 'Trello Boards',
  'Yükleniyor…': 'Loading…',
  'Yapılacak': 'To Do',
  'Devam Ediyor': 'In Progress',
  'İncelemede': 'In Review',
  'Tamamlandı': 'Done',
  'Tamam': 'Done',
  'Görevi sil': 'Delete task',
  'Görev Ekle': 'Add Task',
  'Devamını Gör': 'See More',
  'Kaydet': 'Save',
  'Kaydedildi': 'Saved',
  'Yüksek': 'High',
  'Orta': 'Medium',
  'Düşük': 'Low',
  'Yok': 'None',
  'Genel': 'General',
  'Bugün': 'Today',
  'Dün': 'Yesterday',
  'Açılış tarihi': 'Opened on',
  'Yeni görev': 'New task',
  'Tümü': 'All',
  '" bağlantısı kaldırılsın mı?': '" be unlinked?',
  'Bağlantı kaldırılamadı.': 'Could not remove the connection.',
  'Bağlı hesap': 'Connected account',
  'Kart Çek / Gönder': 'Pull / Push Card',
  'Board Bağla': 'Connect Board',
  "Trello hesabını bağla, board'larını gerçek kanban görünümüyle burada yönet.": 'Connect your Trello account to manage your boards here with a real kanban view.',
  'Trello Hesabını Bağla': 'Connect Trello Account',
  'Bağlantıyı kaldır': 'Remove connection',
  'Açıklama': 'Description',
  'Görev detayı ekle…': 'Add task details…',
  'Kontrol Listesi': 'Checklist',
  'tamamlandı': 'done',
  'Madde ekle…': 'Add item…',
  'Ekle': 'Add',
  'Atanan Kişi': 'Assignee',
  'Atanmadı': 'Unassigned',
  'Bağlantı': 'Link',
  'Etiket (ör. Brief)': 'Label (e.g. Brief)',
  'Platform': 'Platform',
  'Ekler': 'Attachments',
  'Dosya Ekle': 'Add File',
  'Yorumlar': 'Comments',
  'Yorum yaz…': 'Write a comment…',
  'Görevi Sil': 'Delete Task',
});

const API = '/api/v1';

type Priority = 'Yüksek' | 'Orta' | 'Düşük';
type Status = 'todo' | 'doing' | 'review' | 'done';
type ChecklistItem = { id: number; text: string; done: boolean };
type TeamMember = { id: number; name: string; surname: string; email: string; initials: string; avatar_bg: string };
type Comment = { id: number; author_name: string; text: string; created_at: string };
type Attachment = { id: number; filename: string; size: number; content_type: string; created_at: string };
type Task = {
  id: number; status: Status; priority: Priority; platform?: string; platform_bg?: string; platform_color?: string;
  title: string; date_label: string; created_at?: string; avatar: string; avatar_bg?: string;
  description?: string; checklist?: ChecklistItem[];
  assignee_id?: number | null; linked_label?: string; linked_url?: string;
  clear_assignee?: boolean;
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Kartlarda ve detayda görünen tarih = görevin AÇILIŞ tarihi (created_at, UTC). Bugün/Dün ise o kelimeler,
// değilse tarih (bu yıl dışındaysa yıl da). Serbest metin date_label artık gösterilmez/düzenlenmez.
export function openedLabel(iso: string | undefined, lang: 'tr'|'en', fallback: string): string {
  if (!iso) return fallback;
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return fallback;
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(new Date()) - day(d)) / 86400000);
  if (diff === 0) return lang === 'en' ? 'Today' : 'Bugün';
  if (diff === 1) return lang === 'en' ? 'Yesterday' : 'Dün';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatCommentDate(iso: string, lang: 'tr'|'en' = 'tr'): string {
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const PRIO_STYLE: Record<Priority, { bg: string; color: string }> = {
  'Yüksek': { bg: 'var(--py-red-tint)', color: T.redText },
  'Orta': { bg: 'var(--py-amber-tint-alt)', color: T.amberText },
  'Düşük': { bg: 'var(--py-table-divider)', color: T.textMid },
};

const COLUMNS_TR: { key: Status; label: string }[] = [
  { key: 'todo', label: 'Yapılacak' },
  { key: 'doing', label: 'Devam Ediyor' },
  { key: 'review', label: 'İncelemede' },
  { key: 'done', label: 'Tamamlandı' },
];
const COLUMNS_EN: { key: Status; label: string }[] = [
  { key: 'todo', label: 'To Do' },
  { key: 'doing', label: 'In Progress' },
  { key: 'review', label: 'In Review' },
  { key: 'done', label: 'Done' },
];

const PLATFORMS: { label: string; bg: string; color: string }[] = [
  { label: 'Yok', bg: '', color: '' },
  { label: 'Google Ads', bg: 'var(--py-amber-tint-alt)', color: T.amberText },
  { label: 'Meta Ads', bg: 'var(--py-blue-tint)', color: T.blue },
  { label: 'TikTok Ads', bg: 'var(--py-table-divider)', color: T.text },
  { label: 'GA4', bg: 'var(--py-orange-tint)', color: 'var(--py-orange-text)' },
  { label: 'Web', bg: 'var(--py-purple-tint)', color: 'var(--py-purple-text)' },
  { label: 'Genel', bg: 'var(--py-table-divider)', color: T.textMid },
];

export default function Gorevler({ clientId = 1, userInitials = 'U' }: { clientId?: number; userInitials?: string }) {
  const { t, lang } = useLang();
  const COLUMNS = lang==='en' ? COLUMNS_EN : COLUMNS_TR;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'pano' | 'trello'>('pano');
  const [search, setSearch] = useState('');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [dragOverCol, setDragOverCol] = useState<Status | null>(null);
  const [filterAssignee, setFilterAssignee] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<string | null>(null);
  // Sütun başına kaç kart gösterileceği — 10 kart üstü uzun sütunlar sayfayı gereksiz
  // uzatıyordu, artık "Devamını Gör" ile isteğe bağlı 10'ar 10'ar açılıyor.
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  const TASKS_PAGE_SIZE = 10;

  useEffect(() => { axios.get(`${API}/gorevler/team?client_id=${clientId}`).then(r => setTeam(r.data)).catch(() => setTeam([])); }, [clientId]);

  const load = () => {
    axios.get(`${API}/gorevler/?client_id=${clientId}`).then(r => setTasks(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clientId]);
  useTrelloOAuthResult(() => setView('trello'));

  const updateTask = (id: number, patch: Partial<Task>) => {
    setTasks(ts => ts.map(t => t.id === id ? { ...t, ...patch } : t));
    axios.patch(`${API}/gorevler/${id}?client_id=${clientId}`, patch)
      .then(r => {
        setTasks(ts => ts.map(t => t.id === id ? { ...t, ...r.data } : t));
        setEditingTask(t => t && t.id === id ? { ...t, ...r.data } : t);
      })
      .catch(() => load());
  };

  const markDone = (id: number) => updateTask(id, { status: 'done' });

  const removeTask = (id: number) => {
    setTasks(ts => ts.filter(t => t.id !== id));
    axios.delete(`${API}/gorevler/${id}?client_id=${clientId}`).catch(() => load());
    if (editingTask?.id === id) setEditingTask(null);
  };

  const addTask = (status: Status = 'todo') => {
    axios.post(`${API}/gorevler/?client_id=${clientId}`, {
      status, priority: 'Orta', title: t('Yeni görev'), date_label: t('Bugün'), avatar: userInitials,
    }).then(r => { setTasks(ts => [r.data, ...ts]); setEditingTask(r.data); }).catch(() => {});
  };

  const visible = tasks.filter(t => {
    if (search.trim() && !t.title.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterAssignee === 'unassigned' && t.assignee_id) return false;
    if (filterAssignee && filterAssignee !== 'unassigned' && t.assignee_id !== Number(filterAssignee)) return false;
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ScreenHeader
        title={t('Görevler')}
        subtitle={t('Pazarlama görevlerini planla, organize et ve takip et.')}
        right={<Button variant="solid" onClick={() => addTask('todo')}>{t('Yeni Görev')}</Button>}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
        {view !== 'trello' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 240, border: `1px solid ${T.borderMid}`,
              borderRadius: 11, padding: '10px 13px', background: T.surface }}>
              <Icon.search size={16} color="var(--py-text-mid-alt)" sw={2} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Görevlerde ara…')}
                style={{ border: 'none', outline: 'none', fontSize: 13, color: T.text, flex: 1, fontFamily: 'inherit', background: 'transparent' }} />
              <span style={{ fontSize: 11, color: T.textFaint, fontFamily: MONO }}>⌘K</span>
            </div>
            <FilterDropdown label="Atanan" value={filterAssignee} onChange={setFilterAssignee}
              options={[{ value: 'unassigned', label: t('Atanmadı') }, ...team.map(m => ({ value: String(m.id), label: `${m.name} ${m.surname}`.trim() || m.email }))]} />
            <FilterDropdown label="Durum" value={filterStatus} onChange={setFilterStatus}
              options={COLUMNS.map(c => ({ value: c.key, label: c.label }))} />
            <FilterDropdown label="Öncelik" value={filterPriority} onChange={setFilterPriority}
              options={(Object.keys(PRIO_STYLE) as Priority[]).map(p => ({ value: p, label: t(p) }))} />
          </>
        )}
        <div style={{ display: 'flex', gap: 3, background: 'var(--py-track-bg)', borderRadius: 10, padding: 3, marginLeft: view === 'trello' ? 0 : undefined }}>
          {(['pano', 'trello'] as const).map(v => (
            <span key={v} onClick={() => setView(v)} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12.5,
              fontWeight: view === v ? 700 : 600, color: view === v ? T.text : 'var(--py-text-mid-alt)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: view === v ? T.surface : 'transparent', boxShadow: view === v ? '0 1px 2px rgba(20,22,43,.1)' : 'none' }}>
              {v === 'trello' && <Icon.trello size={12} color={view === v ? '#0052CC' : T.textDim} />}
              {t(v === 'pano' ? 'Pano' : 'Trello Panoları')}
            </span>
          ))}
        </div>
      </div>

      {view === 'trello' ? (
        <TrelloBoardsSection clientId={clientId} />
      ) : loading ? (
        <PoyaisSpinner size={36} pad={24}/>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, alignItems: 'start' }}>
        {COLUMNS.map(col => {
          const colTasks = visible.filter(t => t.status === col.key);
          const visibleLimit = visibleCounts[col.key] || TASKS_PAGE_SIZE;
          const shownTasks = colTasks.slice(0, visibleLimit);
          const remaining = colTasks.length - shownTasks.length;
          return (
            <div key={col.key}
              onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
              onDragLeave={() => setDragOverCol(c => c === col.key ? null : c)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverCol(null);
                const id = Number(e.dataTransfer.getData('text/task-id'));
                if (id) updateTask(id, { status: col.key });
              }}
              style={{ borderRadius: 12, background: dragOverCol === col.key ? 'var(--py-track-bg)' : 'transparent',
                padding: dragOverCol === col.key ? 6 : 0, transition: 'background .12s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12, padding: '0 2px' }}>
                <span style={{ fontSize: 13.5, fontWeight: 800 }}>{col.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--py-text-mid-alt)', fontFamily: MONO }}>{colTasks.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {shownTasks.map(tsk => {
                  const done = tsk.status === 'done';
                  const prio = PRIO_STYLE[tsk.priority] || PRIO_STYLE['Orta'];
                  return (
                    <div key={tsk.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/task-id', String(tsk.id)); }}
                      onClick={() => setEditingTask(tsk)}
                      style={{ border: `1px solid ${T.border}`, borderRadius: 13, background: done ? 'var(--py-subtle-bg)' : T.surface,
                        padding: '14px 15px', cursor: 'pointer', opacity: done ? 0.72 : 1, position: 'relative' }}>
                      <button onClick={(e) => { e.stopPropagation(); removeTask(tsk.id); }}
                        style={{ position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: 6, border: 'none',
                          background: 'transparent', color: T.textFaint, cursor: 'pointer', fontSize: 12, lineHeight: 1 }}
                        title={t('Görevi sil')}>✕</button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
                        {done
                          ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'var(--py-green-tint)', color: T.greenText }}>{t('Tamam')}</span>
                          : <span onClick={(e) => { e.stopPropagation(); markDone(tsk.id); }} style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: prio.bg, color: prio.color }}>{t(tsk.priority)}</span>}
                        {!done && tsk.platform && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: tsk.platform_bg, color: tsk.platform_color }}>{t(tsk.platform)}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 11,
                        textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--py-text-mid-alt)' : T.text }}>{tsk.title}</div>
                      {!!tsk.checklist?.length && (() => {
                        const total = tsk.checklist!.length, doneCount = tsk.checklist!.filter(c => c.done).length;
                        return (
                          <div style={{ marginBottom: 11 }}>
                            <div style={{ height: 4, borderRadius: 3, background: 'var(--py-track-bg)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${total ? doneCount / total * 100 : 0}%`, background: T.accent, borderRadius: 3 }} />
                            </div>
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--py-text-mid-alt)' }} title={t('Açılış tarihi')}>{openedLabel(tsk.created_at, lang, tsk.date_label)}</span>
                          {!!tsk.checklist?.length && (
                            <span style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{tsk.checklist!.filter(c => c.done).length}/{tsk.checklist!.length}</span>
                          )}
                        </span>
                        <span style={{ width: 24, height: 24, borderRadius: '50%', background: tsk.avatar_bg || 'linear-gradient(135deg,#2A2D48,#4A4E6D)',
                          color: '#fff', fontSize: 9.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{tsk.avatar}</span>
                      </div>
                    </div>
                  );
                })}
                {remaining > 0 && (
                  <button onClick={() => setVisibleCounts(v => ({ ...v, [col.key]: visibleLimit + TASKS_PAGE_SIZE }))}
                    style={{ border: `1px solid ${T.border}`, borderRadius: 11, padding: '10px 12px', textAlign: 'center' as const,
                      background: 'transparent', fontSize: 12, fontWeight: 700, color: T.accent, cursor: 'pointer' }}>
                    {t('Devamını Gör')} ({remaining})
                  </button>
                )}
                <div onClick={() => addTask(col.key)}
                  style={{ border: '1px dashed var(--py-border-soft)', borderRadius: 11, padding: '10px 12px', textAlign: 'center' as const,
                    fontSize: 12, fontWeight: 700, color: T.textDim, cursor: 'pointer' }}>
                  {t('Görev Ekle')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {editingTask && (
        <TaskPanel
          task={editingTask}
          team={team}
          clientId={clientId}
          onClose={() => setEditingTask(null)}
          onChange={(patch) => setEditingTask(t => t ? { ...t, ...patch } : t)}
          onSave={(patch) => updateTask(editingTask.id, patch)}
          onDelete={() => removeTask(editingTask.id)}
        />
      )}
    </div>
  );
}

function FilterDropdown({ label, options, value, onChange }: {
  label: string; options: { value: string; label: string }[]; value: string | null; onChange: (v: string | null) => void;
}) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const active = options.find(o => o.value === value);
  return (
    <div style={{ position: 'relative' }}>
      <span onClick={() => setOpen(o => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
        border: `1px solid ${active ? T.accent : T.borderMid}`, borderRadius: 11, padding: '10px 14px', fontSize: 12.5, fontWeight: 600,
        color: active ? T.text : T.textMid, cursor: 'pointer',
        background: active ? 'color-mix(in srgb, var(--py-accent) 8%, var(--py-surface))' : T.surface }}>
        {active ? active.label : t(label)}
        <Icon.chevron size={11} sw={2.4} color={T.textMid} />
      </span>
      {open && (<>
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 10, boxShadow: '0 8px 24px rgba(20,22,43,.14)', padding: 4, zIndex: 11, minWidth: 170, maxHeight: 280, overflowY: 'auto' as const }}>
          <button onClick={() => { onChange(null); setOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none',
            borderRadius: 6, padding: '7px 10px', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            background: value === null ? 'var(--py-track-bg)' : 'transparent', color: T.text }}>{t('Tümü')}</button>
          {options.map(o => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left',
              border: 'none', borderRadius: 6, padding: '7px 10px', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              background: value === o.value ? 'var(--py-track-bg)' : 'transparent', color: T.text }}>{o.label}</button>
          ))}
        </div>
      </>)}
    </div>
  );
}

type TrelloBoardRow = { id: number; trello_board_id: string; trello_board_name: string; trello_url: string };

function TrelloBoardsSection({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const { connected, username } = useTrelloStatus(clientId);
  const [boards, setBoards] = useState<TrelloBoardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openBoard, setOpenBoard] = useState<TrelloBoardRow | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);

  const load = () => {
    axios.get(`${API}/trello/boards`, { params: { client_id: clientId } })
      .then(r => setBoards(r.data)).catch(() => setBoards([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (connected) load(); else setLoading(false); }, [connected, clientId]);

  const unlink = (b: TrelloBoardRow) => {
    if (!window.confirm(`"${b.trello_board_name}${t('" bağlantısı kaldırılsın mı?')}`)) return;
    setUnlinkingId(b.id);
    axios.post(`${API}/trello/unlink-board/${b.id}`, null, { params: { client_id: clientId } })
      .then(() => setBoards(bs => bs.filter(x => x.id !== b.id)))
      .catch(() => alert(t('Bağlantı kaldırılamadı.')))
      .finally(() => setUnlinkingId(null));
  };

  if (openBoard) {
    return <TrelloBoardDetail boardRowId={openBoard.id} boardName={openBoard.trello_board_name} trelloBoardId={openBoard.trello_board_id} clientId={clientId} onBack={() => { setOpenBoard(null); load(); }} />;
  }

  if (connected === null || loading) {
    return <PoyaisSpinner size={36} pad={24}/>;
  }

  if (!connected) {
    return (
      <div style={{ border: `1px dashed var(--py-border-soft)`, borderRadius: 16, padding: '48px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--py-blue-tint)', color: '#0052CC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.trello size={22} color="#0052CC" />
        </span>
        <div style={{ fontSize: 13, color: 'var(--py-text-mid-alt)', textAlign: 'center' as const }}>{t("Trello hesabını bağla, board'larını gerçek kanban görünümüyle burada yönet.")}</div>
        <Button variant="solid" onClick={() => connectTrello(clientId, 'gorevler')}>{t('Trello Hesabını Bağla')}</Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' as const }}>
        <span style={{ fontSize: 12, color: T.textDim }}>{t('Bağlı hesap')}: <b style={{ color: T.text }}>@{username}</b></span>
        <div style={{ display: 'flex', gap: 9 }}>
          <Button variant="ghost" onClick={() => setBridgeOpen(true)}>{t('Kart Çek / Gönder')}</Button>
          <Button variant="outline" color="#0052CC" onClick={() => setPickerOpen(true)}>
            <Icon.trello size={14} color="#0052CC" />{t('Board Bağla')}
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        {boards.map(b => (
          <div key={b.id} onClick={() => setOpenBoard(b)}
            style={{ border: `1px solid ${T.border}`, borderRadius: 13, background: T.surface, padding: '18px 20px', cursor: 'pointer', position: 'relative' }}>
            <button onClick={e => { e.stopPropagation(); unlink(b); }} disabled={unlinkingId === b.id}
              style={{ position: 'absolute', top: 10, right: 10, width: 22, height: 22, borderRadius: 7, border: 'none',
                background: 'transparent', color: T.textFaint, cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
              title={t('Bağlantıyı kaldır')}>✕</button>
            <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--py-blue-tint)', color: '#0052CC', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <Icon.trello size={16} color="#0052CC" />
            </span>
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{b.trello_board_name}</div>
          </div>
        ))}
        <div onClick={() => setPickerOpen(true)} style={{ border: '1px dashed var(--py-border-soft)', borderRadius: 13, background: 'transparent',
          padding: '18px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', minHeight: 108 }}>
          <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--py-track-bg)', color: 'var(--py-text-mid-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.plus size={15} sw={2.2} /></span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--py-text-mid-alt)' }}>{t('Board Bağla')}</span>
        </div>
      </div>

      {pickerOpen && <TrelloBoardPickerModal clientId={clientId} onClose={() => setPickerOpen(false)} onLinked={load} />}
      {bridgeOpen && <TrelloBridgeModal clientId={clientId} onClose={() => setBridgeOpen(false)} onImported={() => {}} />}
    </div>
  );
}

const FIELD_LABEL: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.06em' };
const FIELD_INPUT: React.CSSProperties = { width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'var(--py-surface)', boxSizing: 'border-box' };

function TaskPanel({ task, team, clientId, onClose, onChange, onSave, onDelete }: {
  task: Task; team: TeamMember[]; clientId: number; onClose: () => void; onChange: (patch: Partial<Task>) => void;
  onSave: (patch: Partial<Task>) => void; onDelete: () => void;
}) {
  const { t, lang } = useLang();
  const COLUMNS = lang==='en' ? COLUMNS_EN : COLUMNS_TR;
  const [newItem, setNewItem] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const checklist = task.checklist || [];
  const doneCount = checklist.filter(c => c.done).length;
  const prio = PRIO_STYLE[task.priority] || PRIO_STYLE['Orta'];

  useEffect(() => {
    axios.get(`${API}/gorevler/${task.id}/comments?client_id=${clientId}`).then(r => setComments(r.data)).catch(() => setComments([]));
    axios.get(`${API}/gorevler/${task.id}/attachments?client_id=${clientId}`).then(r => setAttachments(r.data)).catch(() => setAttachments([]));
  }, [task.id, clientId]);

  // Başlık/açıklama/tarih SADECE onBlur'da kaydediliyor — kullanıcı odağı input'tan
  // hiç ayırmadan kapatırsa (ör. doğrudan pano dışına tıklayıp kapatma) değişiklik
  // kaybolabilir hissi veriyordu. Bu buton, henüz blur olmamış tüm metin alanlarını
  // tek seferde kaydedip kısa bir "Kaydedildi" onayı gösteriyor.
  const saveAll = () => {
    onSave({ title: task.title, description: task.description });
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 1500);
  };

  const saveChecklist = (list: ChecklistItem[]) => { onChange({ checklist: list }); onSave({ checklist: list }); };
  const toggleItem = (id: number) => saveChecklist(checklist.map(c => c.id === id ? { ...c, done: !c.done } : c));
  const removeItem = (id: number) => saveChecklist(checklist.filter(c => c.id !== id));
  const addItem = () => {
    const text = newItem.trim();
    if (!text) return;
    const id = checklist.reduce((m, c) => Math.max(m, c.id), 0) + 1;
    saveChecklist([...checklist, { id, text, done: false }]);
    setNewItem('');
  };

  const assignTo = (val: string) => {
    if (!val) {
      onChange({ assignee_id: null, avatar: '??', avatar_bg: undefined });
      onSave({ clear_assignee: true });
    } else {
      const id = Number(val);
      const m = team.find(t => t.id === id);
      onChange({ assignee_id: id, avatar: m?.initials, avatar_bg: m?.avatar_bg });
      onSave({ assignee_id: id });
    }
  };

  const sendComment = () => {
    const text = newComment.trim();
    if (!text) return;
    axios.post(`${API}/gorevler/${task.id}/comments?client_id=${clientId}`, { text })
      .then(r => setComments(cs => [...cs, r.data]))
      .catch(() => {});
    setNewComment('');
  };
  const deleteComment = (id: number) => {
    setComments(cs => cs.filter(c => c.id !== id));
    axios.delete(`${API}/gorevler/comments/${id}?client_id=${clientId}`).catch(() => {});
  };

  const uploadFile = (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    axios.post(`${API}/gorevler/${task.id}/attachments?client_id=${clientId}`, fd)
      .then(r => setAttachments(as => [r.data, ...as]))
      .catch(() => {})
      .finally(() => setUploading(false));
  };
  const downloadAttachment = (a: Attachment) => {
    axios.get(`${API}/gorevler/attachments/${a.id}/download?client_id=${clientId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = a.filename;
        link.click();
        window.URL.revokeObjectURL(url);
      }).catch(() => {});
  };
  const deleteAttachment = (id: number) => {
    setAttachments(as => as.filter(a => a.id !== id));
    axios.delete(`${API}/gorevler/attachments/${id}?client_id=${clientId}`).catch(() => {});
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(20,22,43,.35)' }} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '92vw', background: T.surface,
        boxShadow: '-8px 0 32px rgba(20,22,43,.18)', display: 'flex', flexDirection: 'column', overflowY: 'auto' as const, padding: '22px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
            background: task.platform_bg || 'var(--py-table-divider)', color: task.platform_color || T.textMid }}>{t(task.platform || 'Genel')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.textDim, lineHeight: 1 }}>×</button>
        </div>

        <input value={task.title}
          onChange={(e) => onChange({ title: e.target.value })}
          onBlur={() => onSave({ title: task.title })}
          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 19, fontWeight: 800, fontFamily: 'inherit',
            color: T.text, marginBottom: 16, padding: 0 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const, marginBottom: 22 }}>
          <select value={task.priority}
            onChange={(e) => { const priority = e.target.value as Priority; onChange({ priority }); onSave({ priority }); }}
            style={{ border: `1px solid ${prio.color}55`, background: prio.bg, color: prio.color, borderRadius: 999,
              padding: '6px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
            {(Object.keys(PRIO_STYLE) as Priority[]).map(p => <option key={p} value={p}>{t(p)}</option>)}
          </select>
          <span title={t('Açılış tarihi')}
            style={{ border: `1px solid ${T.borderMid}`, borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 600,
              color: T.textMid, whiteSpace: 'nowrap' as const }}>{openedLabel(task.created_at, lang, task.date_label)}</span>
          <span style={{ width: 26, height: 26, borderRadius: '50%', background: task.avatar_bg || 'linear-gradient(135deg,#2A2D48,#4A4E6D)',
            color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{task.avatar}</span>
          <select value={task.status}
            onChange={(e) => { const status = e.target.value as Status; onChange({ status }); onSave({ status }); }}
            style={{ border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '6px 10px', fontSize: 12, fontWeight: 700,
              color: T.text, background: 'var(--py-track-bg)', fontFamily: 'inherit', outline: 'none', marginLeft: 'auto', cursor: 'pointer' }}>
            {COLUMNS.map(c => <option key={c.key} value={c.key}>{t(c.label)}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Açıklama')}</div>
          <textarea value={task.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            onBlur={() => onSave({ description: task.description })}
            placeholder={t('Görev detayı ekle…')} rows={4}
            style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px',
              fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const, background: 'var(--py-subtle-bg)' }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Kontrol Listesi')}</span>
            {checklist.length > 0 && <span style={{ fontSize: 11, color: T.textDim, fontFamily: MONO }}>{doneCount} / {checklist.length} {t('tamamlandı')}</span>}
          </div>
          {checklist.length > 0 && (
            <div style={{ height: 5, borderRadius: 3, background: 'var(--py-track-bg)', overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ height: '100%', width: `${doneCount / checklist.length * 100}%`, background: T.accent, borderRadius: 3 }} />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 6, marginBottom: 10 }}>
            {checklist.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '4px 0' }}>
                <button onClick={() => toggleItem(c.id)} style={{ width: 18, height: 18, borderRadius: '50%',
                  border: `2px solid ${c.done ? T.accent : T.borderMid}`, background: c.done ? T.accent : T.surface, flexShrink: 0,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                  {c.done && <Icon.check size={10} sw={3} color="#fff" />}
                </button>
                <span style={{ flex: 1, fontSize: 12.5, color: c.done ? T.textDim : T.text, textDecoration: c.done ? 'line-through' : 'none' }}>{c.text}</span>
                <button onClick={() => removeItem(c.id)} style={{ background: 'none', border: 'none', color: T.textFaint, cursor: 'pointer', fontSize: 12, padding: 2 }}>✕</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newItem} onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
              placeholder={t('Madde ekle…')}
              style={{ flex: 1, border: `1px solid ${T.borderMid}`, borderRadius: 9, padding: '8px 11px', fontSize: 12.5, fontFamily: 'inherit', outline: 'none' }} />
            <button onClick={addItem} style={{ padding: '8px 14px', borderRadius: 9, border: 'none', background: 'var(--py-ink)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t('Ekle')}</button>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={FIELD_LABEL}>{t('Atanan Kişi')}</div>
          <select value={task.assignee_id ?? ''} onChange={(e) => assignTo(e.target.value)} style={{ ...FIELD_INPUT, cursor: 'pointer' }}>
            <option value="">{t('Atanmadı')}</option>
            {team.map(m => (
              <option key={m.id} value={m.id}>{`${m.name} ${m.surname}`.trim() || m.email}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={FIELD_LABEL}>{t('Bağlantı')}</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: task.linked_url ? 8 : 0 }}>
            <input value={task.linked_label || ''}
              onChange={(e) => onChange({ linked_label: e.target.value })}
              onBlur={() => onSave({ linked_label: task.linked_label })}
              placeholder={t('Etiket (ör. Brief)')} style={{ ...FIELD_INPUT, flex: 1 }} />
            <input value={task.linked_url || ''}
              onChange={(e) => onChange({ linked_url: e.target.value })}
              onBlur={() => onSave({ linked_url: task.linked_url })}
              placeholder="https://…" style={{ ...FIELD_INPUT, flex: 2 }} />
          </div>
          {task.linked_url && (
            <a href={task.linked_url} target="_blank" rel="noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: T.accent, textDecoration: 'none' }}>
              <Icon.link size={13} sw={2} color={T.accent} />{task.linked_label || task.linked_url}
            </a>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Platform')}</div>
          <select value={task.platform || 'Yok'}
            onChange={(e) => {
              const preset = PLATFORMS.find(p => p.label === e.target.value) || PLATFORMS[0];
              const patch = { platform: preset.label === 'Yok' ? undefined : preset.label, platform_bg: preset.bg, platform_color: preset.color };
              onChange(patch as Partial<Task>);
              onSave(patch as Partial<Task>);
            }}
            style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '9px 12px',
              fontSize: 13, fontFamily: 'inherit', outline: 'none', background: T.surface }}>
            {PLATFORMS.map(p => <option key={p.label} value={p.label}>{t(p.label)}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 20, marginTop: 20, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
          <div style={{ ...FIELD_LABEL, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.paperclip size={13} sw={2} color="var(--py-text-mid-alt)" />{t('Ekler')}{attachments.length > 0 ? ` (${attachments.length})` : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 6, marginBottom: 10 }}>
            {attachments.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 9, border: `1px solid ${T.border}`,
                borderRadius: 9, padding: '8px 10px' }}>
                <span onClick={() => downloadAttachment(a)} style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: T.text,
                  cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{a.filename}</span>
                <span style={{ fontSize: 11, color: T.textDim, fontFamily: MONO, flexShrink: 0 }}>{formatSize(a.size)}</span>
                <button onClick={() => deleteAttachment(a.id)} style={{ background: 'none', border: 'none', color: T.textFaint, cursor: 'pointer', fontSize: 12, padding: 2, flexShrink: 0 }}>✕</button>
              </div>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: '1px dashed var(--py-border-soft)',
            borderRadius: 10, padding: '10px 12px', fontSize: 12, fontWeight: 700, color: uploading ? T.textFaint : T.textDim, cursor: uploading ? 'default' : 'pointer' }}>
            <Icon.paperclip size={13} sw={2} color="currentColor" />
            {uploading ? t('Yükleniyor…') : t('Dosya Ekle')}
            <input type="file" disabled={uploading} style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
          </label>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ ...FIELD_LABEL, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.comment size={13} sw={2} color="var(--py-text-mid-alt)" />{t('Yorumlar')}{comments.length > 0 ? ` (${comments.length})` : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10, marginBottom: 10 }}>
            {comments.map(c => (
              <div key={c.id} style={{ background: 'var(--py-subtle-bg)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 11px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{c.author_name}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{formatCommentDate(c.created_at, lang)}</span>
                    <button onClick={() => deleteComment(c.id)} style={{ background: 'none', border: 'none', color: T.textFaint, cursor: 'pointer', fontSize: 12, padding: 0 }}>✕</button>
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: T.textMid, whiteSpace: 'pre-wrap' as const }}>{c.text}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newComment} onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendComment(); }}
              placeholder={t('Yorum yaz…')}
              style={{ flex: 1, border: `1px solid ${T.borderMid}`, borderRadius: 9, padding: '8px 11px', fontSize: 12.5, fontFamily: 'inherit', outline: 'none' }} />
            <button onClick={sendComment} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, borderRadius: 9,
              border: 'none', background: 'var(--py-ink)', color: '#fff', cursor: 'pointer' }}><Icon.send size={14} sw={2.2} color="#fff" /></button>
          </div>
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={onDelete}
            style={{ flex: 1, padding: '10px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface,
              color: T.redText, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('Görevi Sil')}</button>
          <button onClick={saveAll}
            style={{ flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none', background: justSaved ? T.greenText : T.accent,
              color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'background .15s' }}>
            {justSaved ? t('Kaydedildi') : t('Kaydet')}
          </button>
        </div>
      </div>
    </div>
  );
}
